import { isValidWhatsappNumber, whatsappFieldError } from "@picovico/utils";
import type { WhatsAppCheckoutFields, WhatsAppMe } from "custom-types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAppSelector } from "store/hooks";
import { useGetWhatsappMeQuery } from "store/services/whatsapp";
import {
  WhatsAppCheckoutOptions,
  WhatsAppSendKind,
  rememberWhatsAppSent,
  takeWhatsAppSent,
  whatsappCheckoutFields,
  whatsappCheckoutOptions,
  whatsappNumberProblem,
} from "utils/whatsapp";
import { useWebFeature } from "./useWebFeature";

/**
 * The signed-in customer's WhatsApp options (`GET /api/whatsapp/me/`,
 * claude/design/whatsapp-messaging/02 §11.3), or null for "nothing offered".
 * Asked only while the company has `whatsapp` (absent means off) and someone
 * is signed in, and not at all with `skip`. Any refusal (403
 * `FEATURE_DISABLED` once the flag is switched off) or failure also reads as
 * null: WhatsApp only hides its controls, it never blocks a checkout. Read
 * again on every mount, since a checkout opt-in changes `offers.opted_in`.
 */
export function useWhatsAppMe({
  skip = false,
}: { skip?: boolean } = {}): WhatsAppMe | null {
  const enabled = useWebFeature("whatsapp");
  const isLoggedIn = useAppSelector((state) => state.user.isLoggedIn);
  const active = enabled && isLoggedIn && !skip;
  const { data, isError } = useGetWhatsappMeQuery(undefined, {
    skip: !active,
    refetchOnMountOrArgChange: true,
  });
  return active && !isError && data ? data : null;
}

/** A checkout's WhatsApp box (`useWhatsAppCheckout`). */
export interface WhatsAppCheckout {
  kind: WhatsAppSendKind;
  /** What the box offers; `tickets` false hides it. */
  options: WhatsAppCheckoutOptions;
  /**
   * "Send my ticket (order) on WhatsApp": ticked by default (D15) when the
   * profile has a valid mobile to prefill, else unticked, so a customer
   * without one is never stopped at Pay by an empty number.
   */
  send: boolean;
  setSend: (value: boolean) => void;
  /** The number as typed, prefilled once from the profile mobile. */
  number: string;
  setNumber: (value: string) => void;
  /** "Also send me offers on WhatsApp": unticked by default (D15). */
  offers: boolean;
  setOffers: (value: boolean) => void;
  /** The number's problem: the field check, or the checkout's 400 on `whatsapp_to`. */
  error: string | null;
  /**
   * The fields for the checkout body (`{}` when nothing is asked for), or
   * null when the number has to be fixed first; its error then shows under
   * the field.
   */
  prepare: () => WhatsAppCheckoutFields | null;
  /** Shows a checkout refusal on `whatsapp_to` under the number; true when it was one. */
  showServerError: (failure: unknown) => boolean;
  /**
   * Keeps what this payment asked for (or forgets an older one) until the
   * success page, for the payment known by `refs`.
   */
  remember: (fields: WhatsAppCheckoutFields, refs: readonly unknown[]) => void;
}

/**
 * The state of the ticket or food checkout's WhatsApp opt-in (04 §3, pvapp's
 * `WhatsAppOptInController`): shown when `whatsapp.tickets` and `me.tickets`
 * (`me.food` on the food checkout); the offers box needs
 * `whatsapp.promotions`, `me.offers.available` and `me.offers.number` too.
 */
export function useWhatsAppCheckout(kind: WhatsAppSendKind): WhatsAppCheckout {
  const ticketsFlag = useWebFeature("whatsapp.tickets");
  const promotionsFlag = useWebFeature("whatsapp.promotions");
  const me = useWhatsAppMe({ skip: !ticketsFlag });
  const mobile = useAppSelector((state) => state.user.user?.mobile);
  const options = useMemo(
    () => whatsappCheckoutOptions({ kind, ticketsFlag, promotionsFlag, me }),
    [kind, ticketsFlag, promotionsFlag, me],
  );

  // Null until the customer ticks or unticks it: until then it follows
  // whether the profile mobile is a valid number to send to.
  const [sendChoice, setSendChoice] = useState<boolean | null>(null);
  const send = sendChoice ?? isValidWhatsappNumber(mobile);
  const [offers, setOffers] = useState(false);
  const [number, setNumberState] = useState("");
  const [error, setError] = useState<string | null>(null);
  const prefilled = useRef(false);

  // The profile mobile, once, and never over what the customer typed; a
  // profile that loads after the checkout still fills it.
  useEffect(() => {
    const value = (mobile ?? "").trim();
    if (prefilled.current || !value) return;
    prefilled.current = true;
    setNumberState((current) => current || value);
  }, [mobile]);

  const setSend = useCallback((value: boolean) => {
    setSendChoice(value);
    setError(null);
  }, []);

  // The customer edited the number: neither error applies any more.
  const setNumber = useCallback((value: string) => {
    setNumberState(value);
    setError(null);
  }, []);

  const prepare = useCallback((): WhatsAppCheckoutFields | null => {
    if (!options.tickets || !send) return {};
    const problem = whatsappNumberProblem(number);
    if (problem) {
      setError(problem);
      return null;
    }
    return whatsappCheckoutFields(options, { send, number, offers });
  }, [options, send, number, offers]);

  // 400 `{"error": {"whatsapp_to": ["Enter a valid mobile number."]}}` (02 §14).
  const showServerError = useCallback((failure: unknown) => {
    const message = whatsappFieldError(failure, "whatsapp_to");
    if (!message) return false;
    setError(message);
    return true;
  }, []);

  const remember = useCallback(
    (fields: WhatsAppCheckoutFields, refs: readonly unknown[]) =>
      rememberWhatsAppSent(kind, fields.whatsapp_to ?? null, refs),
    [kind],
  );

  return {
    kind,
    options,
    send,
    setSend,
    number,
    setNumber,
    offers,
    setOffers,
    error,
    prepare,
    showServerError,
    remember,
  };
}

const REF_SEPARATOR = "\u0000";

/**
 * The number a just-paid checkout asked to send the ticket (food order) to,
 * for "Your ticket is on its way to WhatsApp (…)": taken from what the
 * checkout kept for the payment known by `refs` once `status` is `success`,
 * and shown once. A failed or expired payment forgets it; a pending one
 * keeps it for the next look.
 */
export function useWhatsAppSentNote(
  kind: WhatsAppSendKind,
  refs: readonly (string | null | undefined)[],
  status: string | null | undefined,
): string | null {
  const [number, setNumber] = useState<string | null>(null);
  const refsKey = refs.filter(Boolean).join(REF_SEPARATOR);

  useEffect(() => {
    if (!refsKey) return;
    const known = refsKey.split(REF_SEPARATOR);
    if (status === "success") {
      const kept = takeWhatsAppSent(kind, known);
      if (kept) setNumber(kept);
    } else if (status === "failed" || status === "expired") {
      takeWhatsAppSent(kind, known);
    }
  }, [kind, refsKey, status]);

  return status === "success" ? number : null;
}
